import type { RTNode } from '@prismicio/client';
import type { AnyRegularField, Slice } from '@prismicio/types';

import type { ReversedAppDomains } from '../constants/config';

/**
 * Set of node types that contain text content.
 */
const textNodeTypes: Set<string> = new Set([
  'heading1',
  'heading2',
  'heading3',
  'heading4',
  'heading5',
  'heading6',
  'paragraph',
  'preformatted',
  'list-item',
  'o-list-item',
  'hyperlink',
]);

/**
 * Replaces URLs in a given text content based on the environment specified in the URLs.
 *
 * @param {string} content - The text content containing URLs to be replaced.
 * @param {ReversedAppDomains | null} replacementUrlMap - It holds the map of URLs that are going to be replaced in the content.
 * @returns {string} The updated text content with replaced URLs.
 */
function replaceUrls(content: string, replacementUrlMap: ReversedAppDomains | null): string {
  if (!replacementUrlMap || !content) {
    return content; // No replacements to be made
  }
  let replacedContent = content;

  for (const prodUrl of Object.keys(replacementUrlMap)) {
    const nonProdUrl = replacementUrlMap[prodUrl];
    replacedContent = replacedContent.split(prodUrl).join(nonProdUrl);
  }

  return replacedContent;
}

/**
 * Replaces URLs in a given Prismic RTNode content based on the specified environment.
 *
 * @param {RTNode} node - The Prismic RTNode content with URLs to be replaced.
 * @param {ReversedAppDomains | null} replacementUrlMap - A mapping of production URLs to non-production URLs.
 */
function replaceUrlsinPrismicNode(node: RTNode, replacementUrlMap: ReversedAppDomains | null) {
  if (typeof node === 'object' && 'text' in node && hasText(node)) {
    const { spans } = node;
    if (spans && spans.length > 0) {
      node.spans = spans.map((span) => {
        if (span.type === 'hyperlink' && typeof span?.data?.url === 'string') {
          span.data.url = replaceUrls(span.data.url, replacementUrlMap);
        }
        return span;
      });
    }

    node.text = replaceUrls(node.text, replacementUrlMap);
  }
}

/**
 * Checks if a given node has text content.
 *
 * @param {RTNode} node - The Prismic RTNode to check.
 * @returns {boolean} True if the node has text content, false otherwise.
 */
function hasText(node: RTNode): boolean {
  return textNodeTypes.has(node.type);
}

/**
 * Recursively replaces URLs in Prismic RTNode content based on the specified environment.
 *
 * @param {[] | [RTNode, ...RTNode[]]} node - The Prismic RTNode content with URLs to be replaced.
 * @param {ReversedAppDomains} replacementUrlMap - It holds the map of URLs that are going to be replaced in the content.
 * @returns {[] | [RTNode, ...RTNode[]} The updated Prismic RTNode content with replaced URLs.
 */
export function replaceUrlsInPrismicContent(
  node: [] | [RTNode, ...RTNode[]],
  replacementUrlMap: ReversedAppDomains | null,
): [] | [RTNode, ...RTNode[]] {
  if (!node || !replacementUrlMap) return node;

  if (Array.isArray(node)) {
    return node.map((childNode) => {
      if (!node) {
        return node;
      } else if (Array.isArray(childNode)) {
        return replaceUrlsInPrismicContent(
          childNode as unknown as [RTNode, ...RTNode[]],
          replacementUrlMap,
        );
      }

      replaceUrlsinPrismicNode(childNode, replacementUrlMap);
      return childNode;
    }) as [] | [RTNode, ...RTNode[]];
  }
  return node;
}

/**
 * Replaces URLs in a given slice based on the environment specified in the URLs.
 *
 * @param {Slice} slice - The slice containing URLs to be replaced.
 * @param {ReversedAppDomains | null} replacementUrlMap - It holds the map of URLs that are going to be replaced in the content.
 * @returns {Slice} The updated slice with replaced URLs.
 */
export function replaceUrlsInSlice(
  slice: Slice,
  replacementUrlMap: ReversedAppDomains | null,
): Slice {
  if (!replacementUrlMap) {
    return slice;
  }

  const updatedSlice: Slice = { ...slice }; // Create a new object to store the updates

  updatedSlice.items = slice.items.map((item: Record<string, AnyRegularField>) => {
    const updatedItem: Record<string, AnyRegularField> = {};

    // Loop through each property in the item object
    for (const key in item) {
      if (Object.prototype.hasOwnProperty.call(item, key)) {
        item[key] = replaceUrlsInPrismicContent(
          item[key] as [RTNode, ...RTNode[]],
          replacementUrlMap,
        ) as AnyRegularField;
      }
    }

    return updatedItem;
  });

  return updatedSlice;
}

/**
 * Replaces URLs in a list of slices based on the environment specified in the URLs.
 *
 * @param {Slice[]} slices - The list of slices containing URLs to be replaced.
 * @param {ReversedAppDomains | null} replacementUrlMap - It holds the map of URLs that are going to be replaced in the content.
 */
export function replaceUrlsInSlices(slices: Slice[], replacementUrlMap: ReversedAppDomains | null) {
  if (!replacementUrlMap) {
    return;
  }
  slices.forEach((slice) => {
    replaceUrlsInSlice(slice, replacementUrlMap);
  });
}
